import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { get } from 'lodash';
import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { Tooltip } from '../ui';
import { InputErrorMessage } from './InputErrorMessage';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
    name: string;
    label?: React.ReactElement<any> | string;
    options?: RegisterOptions;
    className?: string;
    inputClassName?: string;
    defaultValue?: any;
    icon?: IconProp;
    formGroup?: boolean;
    disableErrorMessage?: boolean;
    append?: React.ReactNode | string;
    helperText?: React.ReactNode | string;
}

export const TextInput = ({
    name,
    label,
    options = {},
    className,
    inputClassName,
    defaultValue,
    icon,
    append,
    helperText,
    formGroup = true,
    disableErrorMessage = false,
    onChange,
    onBlur,
    ...props
}: TextInputProps) => {
    const {
        register,
        formState: { errors },
        setValue
    } = useFormContext();
    const [showTooltip, setShowTooltip] = useState(false);
    const error = get(errors, name);

    const wrapInputGroup = (content) => {
        let wrappedContent = content;

        if (disableErrorMessage) {
            wrappedContent = (
                <Tooltip
                    onShow={(nextShow) => setShowTooltip(nextShow)}
                    show={Boolean(error?.message && showTooltip)}
                    tooltip={error?.message}
                >
                    {wrappedContent}
                </Tooltip>
            );
        }

        if (append) {
            wrappedContent = (
                <div className="input-group">
                    {wrappedContent}
                    <div className="input-group-append">
                        {typeof append === 'object' ? append : <span className="input-group-text">{append}</span>}
                    </div>
                </div>
            );
        }

        return wrappedContent;
    };

    useEffect(() => {
        if (defaultValue) setValue(name, defaultValue);
    }, [defaultValue, name, setValue]);

    return (
        <div className={(formGroup ? 'form-group ' : '') + (className ? className : '')}>
            {label && (
                <label>
                    {icon && <FontAwesomeIcon className="mr-2" icon={icon} />}
                    {label}
                </label>
            )}
            {wrapInputGroup(
                <input
                    {...register(name, { onChange, onBlur, ...options })}
                    name={name}
                    type="text"
                    className={'form-control' + (error ? ' is-invalid' : '')}
                    {...props}
                />
            )}
            {helperText && <small className="form-text text-muted">{helperText}</small>}
            {!disableErrorMessage && <InputErrorMessage name={name} />}
        </div>
    );
};
